import type { CityConfig } from "../city-template/types";

export const dearbornConfig: CityConfig = {
  city: "Dearborn",
  state: "Michigan",
  stateAbbr: "MI",
  slug: "dearborn",
  route: "/cities/dearborn",
  title: "Emergency Passport Dearborn MI | Expedited Passport Processing | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Dearborn, Michigan — as fast as 24 hours. Fast passport renewal and new passport applications for Dearborn, Dearborn Heights, Allen Park, Melvindale, and all Wayne County residents. Detroit Passport Agency courier. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Dearborn, MI",
  heroSubtitle: "Dearborn travelers — from Ford Motor Company headquarters to the vibrant Arab-American community to Henry Ford attractions — trust Fast Passport Center for emergency and expedited passports as fast as 24 hours. Detroit Passport Agency is just 15 minutes east. A+ BBB rated courier service serving all of Wayne County.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Downtown%20Dearborn%20Michigan%20streetscape%20with%20historic%20architecture%20and%20modern%20buildings%20along%20Michigan%20Avenue%2C%20Ford%20Motor%20Company%20world%20headquarters%20visible%20in%20the%20background%2C%20tree-lined%20urban%20street%20with%20American%20and%20international%20flags%2C%20warm%20afternoon%20light%2C%20diverse%20community%20atmosphere%2C%20no%20text%20no%20people%2C%20photorealistic%20architectural%20photography%20with%20warm%20earth%20tones&width=1800&height=700&seq=dearborn-hero-001&orientation=landscape",
  heroImageAlt: "Downtown Dearborn Michigan streetscape with Michigan Avenue and Ford headquarters in the background",
  geoPosition: "42.3223;-83.1763",
  geoPlacename: "Dearborn, Michigan",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Detroit Passport Agency",
    address: "211 W. Fort St, Suite 1300, Detroit, MI 48226",
    distance: "Approximately 15–20 minutes east via I-94 or Michigan Avenue — appointments required, typically booked 2 weeks out",
  },
  nearbyCitiesZips: [
    { name: "Dearborn", zips: "48120, 48121, 48123, 48124, 48125, 48126, 48128" },
    { name: "Dearborn Heights", zips: "48125, 48127" },
    { name: "Allen Park", zips: "48101" },
    { name: "Melvindale", zips: "48122" },
    { name: "Lincoln Park", zips: "48146" },
    { name: "Riverview", zips: "48193" },
    { name: "Wyandotte", zips: "48192" },
    { name: "Taylor", zips: "48180" },
    { name: "Southgate", zips: "48195" },
    { name: "Inkster", zips: "48141" },
    { name: "Westland", zips: "48185, 48186" },
    { name: "Garden City", zips: "48135" },
    { name: "Romulus", zips: "48174" },
    { name: "Wayne", zips: "48184" },
    { name: "Redford", zips: "48239, 48240" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier to the Detroit Passport Agency — no appointment wait" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-global-line", title: "Dearborn Community Expertise", desc: "Serving Dearborn's diverse international community with cultural sensitivity" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — stay home while we do the work" },
  ],
  internalLinks: [
    { name: "Expedited passport in Detroit, MI", route: "/cities/detroit" },
    { name: "Emergency passport services in Warren, MI", route: "/cities/warren" },
    { name: "Urgent passport services in Taylor, MI", route: "/states/michigan" },
    { name: "Michigan passport services overview", route: "/states/michigan" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Dearborn, Michigan via secure registered courier and has direct access to the Detroit Passport Agency at 211 W. Fort St, Suite 1300. Our registered couriers hand-deliver your documents — meaning you get faster results without the appointment wait. Dearborn residents can ship from any UPS or FedEx in Wayne County.",
  fpcOfficeDistance: "Serving Dearborn, Dearborn Heights, Allen Park, Melvindale, Lincoln Park, Riverview, Wyandotte, Taylor, Southgate, Inkster, Westland, Garden City, Romulus, Wayne, and Redford",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Dearborn and the greater western Wayne County area. From the Ford Motor Company headquarters to the Arab-American National Museum district to the historic Ford Rouge Plant — we serve all of Dearborn and western Wayne County. Dearborn is home to one of the largest Arab-American communities in the United States, and our services are tailored to meet the international travel needs of this vibrant community.",
  nearbyCitiesIntro: "We serve Dearborn and all western Wayne County communities including Dearborn Heights, Allen Park, Melvindale, Lincoln Park, Riverview, Wyandotte, Taylor, Southgate, Inkster, Westland, Garden City, Romulus, Wayne, and Redford. We also serve neighboring Detroit and Oakland county communities.",
  howItWorksIntro: "Most Dearborn travelers complete everything online and ship documents overnight. No waiting weeks for a passport agency appointment — our registered couriers handle the Detroit Passport Agency submission for you.",
  mistakesIntro: "Dearborn has a large international community with many first-time passport applicants and families visiting relatives abroad. Here are the most common passport mistakes we see from Dearborn travelers.",
  testimonialsIntro: "Dearborn travelers — from Ford engineers to small business owners to families visiting relatives in the Middle East — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Dearborn, MI? Here are the most common ones we hear from Dearborn and Wayne County travelers.",
  statePageRoute: "/states/michigan",
  statePageName: "Michigan",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Detroit Passport Agency (Self-Service)",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — typically 2 weeks out", "Life-or-death emergencies only", "Must appear in person downtown Detroit", "Full day commitment — parking + wait time"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Direct access to Detroit Passport Agency", "No appointment wait — we handle submission", "Perfect for family and international travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Direct access to Detroit Passport Agency", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Dearborn travelers with immediate departure needs — as fast as 24 hours. Our couriers go directly to the Detroit Passport Agency. Perfect for family emergencies, business trips, and urgent international travel.",
      bullets: ["24–48 hour turnaround available", "Registered courier to Detroit Passport Agency", "Family and business travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Dearborn adults with a passport issued within the last 15 years. Skip the post office appointment entirely.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to your Dearborn address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Dearborn residents and families — including first-generation citizens, new families, and Dearborn residents applying for their first passport.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Dearborn residents who lost their passport before international travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Dearborn-area families planning international vacations or visiting family abroad.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Dearborn and Wayne County residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Dearborn MI", description: "Urgent passport processing for Dearborn Michigan travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Dearborn MI", description: "Fast passport renewal for Dearborn Michigan residents with a previously-issued passport." },
    { name: "First-Time Passport Dearborn MI", description: "New passport application services for Dearborn Michigan residents applying for their first passport." },
    { name: "Child Passport Dearborn MI", description: "Minor passport services for Dearborn Michigan families with children under 16." },
  ],
  testimonials: [
    {
      name: "Hassan A.",
      location: "Dearborn, MI",
      rating: 5,
      text: "Emergency visit to family in Lebanon. Needed 4 passports for my family in 72 hours. Fast Passport Center delivered all four in exactly 3 days. Their team was respectful and understanding of our situation.",
      service: "Family Emergency Passport",
    },
    {
      name: "Jennifer F.",
      location: "Dearborn Heights, MI",
      rating: 5,
      text: "Ford business trip to Germany in 5 days and my passport expired. Fast Passport Center handled the renewal in 48 hours. I shipped from the FedEx on Ford Road and everything went perfectly.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "Omar K.",
      location: "Dearborn, MI",
      rating: 5,
      text: "First-time passport at 45 for a pilgrimage trip. I had no idea where to start. Fast Passport Center scheduled my acceptance facility appointment and handled everything after that. The whole process was stress-free.",
      service: "First-Time Passport",
    },
    {
      name: "Linda R.",
      location: "Allen Park, MI",
      rating: 5,
      text: "Cruise to the Caribbean with my grandchildren and discovered my passport was damaged. Fast Passport Center got me a replacement in 3 days. We made the cruise with a day to spare.",
      service: "Damaged Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "dearborn-1",
      name: "Detroit Passport Agency",
      type: "Regional Passport Agency",
      address: "211 W. Fort St, Suite 1300",
      city: "Detroit",
      state: "MI",
      zip: "48226",
      phone: "(313) 226-7110",
      hours: "Mon–Fri: 8:00 AM – 3:00 PM (by appointment only)",
      distance: "Approximately 15–20 minutes east of Dearborn via I-94 or Michigan Avenue",
      website: "https://travel.state.gov/content/travel/en/passports/get-fast/passport-agencies.html",
      websiteLabel: "Schedule Appointment",
      notes: "Michigan's only regional passport agency. Serves travelers with international travel within 14 days (or 28 days with visa). Appointments are required — no walk-ins. Fast Passport Center couriers have direct access and can skip the appointment backlog.",
      icon: "ri-government-line",
    },
    {
      id: "dearborn-2",
      name: "Dearborn Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "19660 Ford Rd",
      city: "Dearborn",
      state: "MI",
      zip: "48128",
      phone: "(313) 943-4311",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Central Dearborn — near Ford Road and Telegraph",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Dearborn acceptance facility. Popular with Dearborn's large international community. Ample parking. Serves Dearborn, Dearborn Heights, and surrounding areas.",
      icon: "ri-mail-line",
    },
    {
      id: "dearborn-3",
      name: "Dearborn Heights Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "23000 Michigan Ave",
      city: "Dearborn Heights",
      state: "MI",
      zip: "48127",
      phone: "(313) 563-5010",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Dearborn Heights — approximately 3 miles west of Dearborn",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Dearborn Heights and western Dearborn residents. Call to confirm current passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "dearborn-4",
      name: "Taylor Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "8500 Telegraph Rd",
      city: "Taylor",
      state: "MI",
      zip: "48180",
      phone: "(313) 291-8300",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Taylor — approximately 6 miles south of Dearborn",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves southern Dearborn and Taylor residents. Call to confirm current passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "dearborn-5",
      name: "Westland Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "33300 Warren Rd",
      city: "Westland",
      state: "MI",
      zip: "48185",
      phone: "(734) 525-3300",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Westland — approximately 8 miles northwest of Dearborn",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves northwestern Dearborn and Westland residents. Call to confirm current passport acceptance hours.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — typically 2 weeks out", fpc: "No — we handle the Detroit agency submission" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "In-Person Requirement", agency: "Yes — must appear at 211 W. Fort St", fpc: "No — ship from anywhere in Dearborn" },
    { feature: "Dearborn Community Expertise", agency: "No tailored advice", fpc: "Yes — serving Arab-American and international community" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Dearborn families often discover passport issues right before planned travel to visit relatives abroad. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your trip.",
    },
    {
      icon: "ri-passport-line",
      title: "Using a Passport Card for Air Travel",
      description: "Many Dearborn residents use a passport card for land crossings to Canada — but these are not valid for air travel. If you fly internationally, you need a passport book.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Passport Photo",
      description: "Local photo shops sometimes produce photos that meet ID requirements but not passport standards. Photos must be exactly 2x2 inches with a plain white or off-white background, taken within 6 months.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form for New Passports",
      description: "Missing signatures or incorrect form completion are the #1 rejection reasons at Dearborn-area acceptance facilities. Our document review catches these before submission.",
    },
    {
      icon: "ri-global-line",
      title: "Not Checking Visa Requirements for Middle East Travel",
      description: "Many Dearborn travelers visit family in Lebanon, Jordan, Iraq, and other Middle Eastern countries. These destinations often require visas in addition to passports. Check requirements early.",
    },
    {
      icon: "ri-time-line",
      title: "Assuming the Detroit Agency Has Walk-In Slots",
      description: "The Detroit Passport Agency requires appointments and is typically booked 2 weeks out. There are no same-day walk-in slots — plan ahead or use Fast Passport Center's courier service.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from anywhere in Dearborn or Wayne County." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx in Dearborn or Wayne County." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Our registered courier hand-delivers to the Detroit Passport Agency — no appointment wait required." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Dearborn address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Where is the Detroit Passport Agency from Dearborn?", a: "The Detroit Passport Agency is located at 211 W. Fort St, Suite 1300, Detroit, MI 48226, approximately 15–20 minutes east of Dearborn via I-94 or Michigan Avenue. It is open Monday through Friday 8:00 AM – 3:00 PM by appointment only. Appointments are typically booked 2 weeks out. Fast Passport Center's registered couriers have direct access to submit applications without individual appointments." },
    { q: "How fast can I get an emergency passport in Dearborn?", a: "Fast Passport Center offers emergency passport processing in Dearborn, Michigan in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Detroit Passport Agency — without you needing to take a day off work or drive to downtown Detroit." },
    { q: "Can I get an expedited passport in Dearborn suburbs like Dearborn Heights, Allen Park, or Melvindale?", a: "Absolutely. Fast Passport Center serves all of western Wayne County including Dearborn, Dearborn Heights, Allen Park, Melvindale, Lincoln Park, Riverview, Wyandotte, Taylor, Southgate, Inkster, Westland, Garden City, Romulus, Wayne, and Redford. Our courier service works the same way everywhere — complete online, ship documents, we handle the rest." },
    { q: "What airports do Dearborn-area travelers use for international flights?", a: "Detroit Metro Wayne County Airport (DTW) in Romulus is the primary international departure hub for Dearborn and western Wayne County travelers. DTW is approximately 15–20 minutes from Dearborn via I-94. It offers direct flights to London, Amsterdam, Tokyo, Paris, Mexico City, Cancun, and dozens of other international destinations. Always verify your passport is valid for at least 6 months past your return date." },
    { q: "How much does an expedited passport cost in Dearborn, MI?", a: "Government fees are fixed: $130 for a first-time adult passport book or $130 + $60 expedite fee for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your specific departure timeline." },
    { q: "Can Dearborn residents get passport photos taken locally?", a: "Yes. Walgreens and CVS locations throughout Dearborn and Wayne County offer passport photos. UPS Stores, FedEx Office locations, and many AAA offices also provide passport photos. Photos must be 2x2 inches on a white or off-white background, taken within the last 6 months." },
    { q: "Does Fast Passport Center serve the Upper Peninsula of Michigan?", a: "Yes. Fast Passport Center's courier service is especially valuable for Upper Peninsula residents, who would otherwise face an 8+ hour drive to the Detroit Passport Agency. We serve Marquette, Sault Ste. Marie, Escanaba, Houghton, and all UP communities — you just ship your documents overnight and we handle everything from there." },
    { q: "What is the best way for Dearborn residents to get to the Detroit Passport Agency?", a: "The fastest route from Dearborn to the Detroit Passport Agency is east on I-94 to downtown Detroit, then to 211 W. Fort St in the Patrick V. McNamara Federal Building. However, you don't need to go there yourself — Fast Passport Center's registered couriers handle the in-person submission for you. You simply ship your documents from any UPS or FedEx in Dearborn or Wayne County." },
  ],
  photoLocations: [
    { name: "Walgreens — Dearborn Ford", address: "24210 Ford Rd, Dearborn, MI 48128", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Dearborn Michigan", address: "22011 Michigan Ave, Dearborn, MI 48124", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Dearborn Heights", address: "23000 Michigan Ave, Dearborn Heights, MI 48127", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "FedEx Office — Allen Park", address: "16800 Southfield Rd, Allen Park, MI 48101", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    { name: "Michigan SOS — Dearborn", address: "3200 Greenfield Rd, Dearborn, MI 48120", hours: "Mon–Fri: 9:00AM–5:00PM", note: "Michigan Secretary of State office for driver's licenses, REAL ID, and vehicle registration. Serves Dearborn and western Wayne County. Reserve a spot online to avoid wait times.", featured: true },
    { name: "Michigan SOS — Taylor", address: "23300 Eureka Rd, Taylor, MI 48180", hours: "Mon–Fri: 9:00AM–5:00PM", note: "Serves Taylor, Dearborn Heights, and southern Wayne County communities. Full REAL ID processing available.", featured: false },
  ],
  airports: [
    {
      code: "DTW",
      name: "Detroit Metro Wayne County Airport",
      distance: "15–20 minutes from Dearborn via I-94",
      address: "1 Detroit Metro Airport, Romulus, MI 48242",
      note: "Primary international hub for Dearborn and western Wayne County travelers. Delta Air Lines hub. Direct flights to London, Amsterdam, Tokyo, Paris, Mexico City, Cancun, and 40+ international destinations. Largest airport in Michigan. Very convenient for Dearborn residents.",
      flightUrl: "https://www.metroairport.com/",
      flightLabel: "DTW Flight Info",
      featured: true,
    },
    {
      code: "YQG",
      name: "Windsor Airport (Canada)",
      distance: "20–25 minutes from Dearborn via Ambassador Bridge",
      address: "3200 County Rd 42, Windsor, ON N9A 6J3, Canada",
      note: "Secondary option for Dearborn travelers crossing to Canada first. Smaller airport with limited international routes. Requires passport for air travel to/from Canada.",
      flightUrl: "https://www.yqg.ca/",
      flightLabel: "Windsor Airport Info",
      featured: false,
    },
  ],
};